/**
 * matchShortCodes — deterministic short-code → doctor-id resolution.
 *
 * AI-SPEC §4 reference implementation.
 *
 * Critical failure mode #4 (AI-SPEC §1):
 *   "Bad short-code match — matching a short code to the wrong doctor (or
 *   fabricating a match) instead of leaving it unmatched for manual fix."
 *
 * Contract: an unmatched code produces null + attendingMatched: false.
 * NEVER a guessed doctor id. A confident wrong match is worse than an honest null.
 *
 * This module is a pure domain function — no Firebase imports, no network,
 * no AI calls. It mirrors the pattern in src/domain/staff.js and
 * src/domain/providerCodes.js (matchCodesToUsers).
 */

/**
 * Match each row's `attendingShortCode` against the stored `shortCode`
 * field on the provided doctor records (case-insensitive exact match).
 *
 * @param {Array<object>} rows
 *   Validated AssignmentRow objects from AssignmentGridSchema.parse() + any
 *   extra fields from prior pipeline steps. Each row has at minimum:
 *     { room, startTime, attendingShortCode, callOrder, isAIC, staffing }
 *
 * @param {Array<{id: string, shortCode?: string, [key: string]: any}>} doctors
 *   Doctor records from the Firestore staff collection (or test fixtures).
 *   Each record must have `id`. Records without a `shortCode` field are skipped.
 *
 * @returns {Array<object>}
 *   A new array of rows, each spread with two added fields:
 *     - attendingDoctorId {string|null}  — matched doctor's `id`, or null when unmatched
 *     - attendingMatched  {boolean}      — true only on an exact (case-insensitive) match
 *
 *   The original row objects are NOT mutated.
 */
export function matchShortCodes(rows, doctors) {
  // Build a case-insensitive Map: UPPER_CODE → doctor record.
  // Doctors without a shortCode field are intentionally skipped — they cannot
  // match any code and including them would risk accidental collisions on undefined.
  const byCode = new Map(
    doctors
      .filter((d) => d.shortCode != null && d.shortCode !== "")
      .map((d) => [d.shortCode.toUpperCase(), d])
  );

  return rows.map((row) => {
    // null / empty attendingShortCode → no match possible
    const code = row.attendingShortCode;
    const doc = code ? byCode.get(code.toUpperCase()) : undefined;

    return {
      ...row,
      // null, never a guess — AI-SPEC §4 failure mode #4
      attendingDoctorId: doc?.id ?? null,
      attendingMatched: Boolean(doc),
    };
  });
}
